import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Anchor, Badge, Group, Paper, Stack, Text, Title } from "@mantine/core"
import { Link } from "@tanstack/react-router"
import { formatRelative } from "../../../lib/format-date"
import classes from "../styles.module.css"
import type { ISeriesSummary, IViewProps } from "./interfaces"

/**
 * Dumb view for RecurringSeriesCard. The smart wrapper at ../index.tsx
 * fetches the series summary and decides whether to render the widget;
 * this view receives the already-loaded series array and renders the
 * dashboard list.
 *
 * Returns null when there's no meaningful series data to show, matching
 * the dashboard policy "don't surface this widget against an empty
 * state". The smart wrapper independently early-exits before mounting
 * this view in that case.
 */
export function RecurringSeriesCardView({ series }: IViewProps): React.JSX.Element | null {
  const hasMeaningfulSeries = series.some((s) => s.meetingCount >= 3)
  if (!hasMeaningfulSeries) return null

  const visible = series.slice(0, 5)

  return (
    <Paper withBorder p="md" mb="lg">
      <Group justify="space-between" mb="xs">
        <Title order={4}>
          <Trans>Recurring meetings</Trans>
        </Title>
        {series.length > 0 && (
          <Text size="xs" c="dimmed">
            <Trans>{series.length} series</Trans>
          </Text>
        )}
      </Group>

      <Stack gap={2} className={classes.list}>
        {visible.map((s) => (
          <SeriesRow key={s.id} summary={s} />
        ))}
      </Stack>
    </Paper>
  )
}

function useFormatRelative(): (iso: string | null) => string {
  const { i18n } = useLingui()
  return (iso) => {
    if (!iso) return i18n._(msg`no meetings yet`)
    const then = new Date(iso).getTime()
    if (!Number.isFinite(then)) return i18n._(msg`no meetings yet`)
    return formatRelative(i18n, Date.now() - then, { dayGranular: true })
  }
}

function SeriesRow({ summary: s }: { summary: ISeriesSummary }): React.JSX.Element {
  const formatRelative = useFormatRelative()
  const linkTarget = s.lastMeetingId ? { to: "/meetings/$id" as const, params: { id: s.lastMeetingId } } : null

  const inner = (
    <Group justify="space-between" wrap="nowrap" className={classes.row}>
      <Stack gap={0} style={{ minWidth: 0 }}>
        <Text fw={600} size="sm" truncate>
          {s.name}
        </Text>
        <Text size="xs" c="dimmed">
          <Trans>
            {s.meetingCount} meetings · last {formatRelative(s.lastMeetingAt)}
          </Trans>
        </Text>
      </Stack>
      {s.openActionItems > 0 ? (
        <Badge color={s.openActionItems >= 5 ? "warning" : "info"} variant="light" size="lg">
          <Trans>{s.openActionItems} open</Trans>
        </Badge>
      ) : s.meetingCount > 0 ? (
        <Badge color="success" variant="light" size="lg">
          <Trans>all clear</Trans>
        </Badge>
      ) : (
        <Badge color="gray" variant="light" size="lg">
          <Trans>new</Trans>
        </Badge>
      )}
    </Group>
  )

  if (linkTarget) {
    return (
      <Anchor
        renderRoot={(props) => <Link to={linkTarget.to} params={linkTarget.params} {...props} />}
        underline="never"
        className={classes.anchor}
      >
        {inner}
      </Anchor>
    )
  }
  return <div className={classes.anchor}>{inner}</div>
}
